/**
 * SHSAT Adaptive — test-taking page.
 *
 * Style-mirrors the legacy ``Module/index.tsx`` flow by reusing its CSS
 * classes (``digitalSHSATModule__*``) so the visual treatment matches.
 * Passage rendering, KaTeX (with ``trust: true`` so ``\htmlClass``
 * markers like ``shsat-paragraph`` survive), and glossary
 * ``<mark>``-insertion mirror the legacy DOM walk verbatim.
 *
 * NO-SKIP RULE: students must submit an answer to advance. The submit
 * button is disabled until the student picks a choice (MC) or fills
 * the input (other types — Phase E follow-up). This is enforced at
 * three layers; see ``memory/feedback_shsat_adaptive_no_skip.md``.
 *
 * Deferred to a follow-up Phase E pass (the smoke harness covered the
 * happy path; full polish needs more):
 *   - Question types other than MC (hot-text + fill-in-the-blank).
 *   - Annotations / highlight tools panel from legacy Module.
 *   - Timer + auto-save (the existing Module has timeLeft handling).
 *   - Result/review screen mount at module-complete.
 *   - Cross-out + answer-eliminator tool.
 *   - Mark-for-review button.
 *
 * What's covered today:
 *   - Auth-gated route to the adaptive serve/save endpoints.
 *   - Mirrored layout, typography, and color via existing CSS.
 *   - KaTeX trust=true → paragraph badges + math symbols render.
 *   - Glossary marks (dotted underline + popup) via DOM walk.
 *   - MC question rendering + radio selection.
 *   - Hard-enforced no-skip on the Next button.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoMdRadioButtonOn, IoMdRadioButtonOff, IoMdMove } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { SiZenn } from 'react-icons/si';
import { dragMove, popupResize } from '../lineReaderDrag';

import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import 'katex/dist/katex.min.css';
// MathLive registers the <math-field> custom element on import. Loaded
// here so types 11 + 12 (Equation Editor) can render.
import 'mathlive';

import { MULTIPLE_CHOICES } from 'global/constants';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import Button from 'components/Button';

import AnnotationSHSATHelper from 'components/AnnotationSHSATHelper';
import LoadingPopup from 'components/LoadingPopup';

import { useAdaptiveFlow, SubmitOptions } from './useAdaptiveFlow';
import { buildPassageWithMarks, PassageIndex } from './buildPassageWithMarks';
import { AdaptiveTopBar, AdaptiveTool } from './AdaptiveTopBar';
import { GlobalNotepad } from './GlobalNotepad';
import { PassageInterstitial } from '../PassageInterstitial';
import { PassageSetStart } from '../PassageSetStart';
import { MagnifierLens } from '../MagnifierLens';
import { SectionDirectionsScreen } from '../SectionDirectionsScreen';
import { resolveSectionDirections, READING_COMP_DIRECTIONS } from '../sectionDirections';
import { STANDALONE_ITEMS_LABEL } from '../sectionOrder';
import {
  PassageAnnotation,
  measureSelectionOffsets,
  generatePassageHTML,
  ANNOTATION_COLOR_CLASS,
} from './passageHighlight';
import {
  BlankPair,
  BucketPair,
  BucketPayload,
  DnDItem,
  DragAndDropBlankHelper,
  DragAndDropBucketHelper,
  EquationEditorHelper,
  FillInPair,
  FillInTheBlankHelper,
  HotTextHelper,
  InlineChoiceHelper,
  InlineChoicePair,
  QuestionHelperPayload,
  TableGridCell,
  TableGridHelper,
  TableGridPair,
} from './QuestionRenderers';

// Reuse the legacy SHSAT styles + the new .shsat-paragraph rule.
import '../Module/index.css';
import '../Module/DragAndDropBucket/index.css';
import '../Module/DragAndDropBlank/index.css';
import '../Module/TableGrid/index.css';
import '../index.css';


interface Choice {
  id: number;
  choice: string;
  figure?: string | null;
}

interface PassagePayload {
  id: string;
  passage: string;
  introduction?: string | null;
  indices: PassageIndex[];
  passageFigures?: Array<{
    figure: { id: string; image: string | null; ttsDescription: string | null };
    index: number;
  }>;
}

interface AdaptiveQuestion {
  id: number;
  questionType: number;
  question: string;
  questionNum: number;
  passages?: PassagePayload | null;
  figures?: Array<{ id: string; image: string | null }>;
  choices?: Choice[];
  questionHelper?: QuestionHelperPayload | null;
  inputSlots?: number;
  /** Max spans selectable on HotText / multi-response / hot-spot
   * questions (= count of correct answers). Triggers the legacy
   * "max N choices" alert when exceeded. */
  maxSelections?: number;
  /** Inline-choice (type 9) dropdown groups: one entry per dropdown
   * inline in ``questionHelper.helper``. */
  inlineChoices?: Array<{ index: number; options: string[] }>;
  /** D&D Bucket (type 5) — list of named buckets to drop items into. */
  buckets?: BucketPayload[];
  /** D&D Bucket (type 5) and D&D Blank (type 6) — draggable items. */
  dragAndDropItems?: DnDItem[];
  /** Table Grid (type 8). */
  tableGridRows?: TableGridCell[];
  tableGridColumns?: TableGridCell[];
  /** Number of ``[[...]]`` markers in the type-6 helper text. The
   * server pre-computes this so the no-skip gate can require every
   * blank to be filled without re-parsing the helper here. */
  blankCount?: number;
}


/**
 * Prop-driven entry point. Used by ``DigitalSHSAT/index.tsx`` to mount
 * the adaptive flow inside the existing student route once the bulk
 * legacy endpoint reports ``adaptive: true``.
 *
 * ``onModuleComplete`` (optional) lets the parent own the cross-module
 * transition: when this module's last slot is submitted, the callback
 * fires and the parent typically remounts AdaptiveModuleInner with the
 * next module's id (via a key change). When ``onModuleComplete`` is
 * NOT provided, the component shows its own terminal "module complete"
 * screen — used by the standalone deeplink route.
 */
export function AdaptiveModuleInner(props: {
  studentTestId: string;
  moduleId: string;
  /** Test name shown in the top bar. */
  testName?: string | null;
  /** Section name (e.g. "ELA") shown next to the test name. */
  sectionName?: string | null;
  /** Module duration in seconds; null = untimed. (Legacy parity: the
   *  active timer is the TOTAL clock below, not per-module — kept for
   *  back-compat / display.) */
  moduleDurationSeconds?: number | null;
  /** TOTAL test duration in seconds across all modules; null = untimed.
   *  The top-bar clock counts this down continuously across modules
   *  (test-wide localStorage key), matching the legacy total timer. */
  totalDurationSeconds?: number | null;
  /** Fired when the total clock reaches 0:00 — parent auto-submits the
   *  whole test, matching legacy's timer-expiry auto-submit. */
  onTimeExpired?: () => void;
  /** First TEST-GLOBAL slot number of THIS module (slot numbers are
   * global in BACKEND order: ELA 1..N, Math N+1..2N). Lets the top bar
   * convert the global slot into a within-module position for the
   * per-section "N OF M" label. */
  moduleFirstSlot?: number;
  /** Sum of slot counts across all modules in the test. */
  totalSlots?: number;
  /** Slot count of THIS module only — drives the per-section "N OF M"
   * header total ("MATH / 1 OF 50" instead of the test-global "51 OF 100"). */
  moduleSlotCount?: number;
  /** Student name shown in the top-right user button. */
  studentName?: string | null;
  /** Called when the last slot of this module is answered. */
  onModuleComplete?: () => void;
  /** Fired the first time this module serves a slot whose navigability
   * carries a non-null ``standaloneItems`` (i.e. the module HAS an ELA
   * "Stand alone items" block). Lets the container title the end-of-section
   * screen precisely ("End of ELA – Stand alone items" only when a block
   * exists), matching the legacy ``isElaSection && standaloneBlocks.length > 0``
   * check. Math / pure-reading modules never fire it. */
  onStandaloneItemsSeen?: () => void;
  /** Opens the container-owned Contrast Settings modal (theming tool). */
  onOpenContrast?: () => void;
  /** "Discard and Exit" — data-destructive discard (parent hits the adaptive
   * discard endpoint, clears localStorage, navigates). */
  onDiscardExit?: () => void;
}): JSX.Element {
  const flow = useAdaptiveFlow({
    studentTestId: props.studentTestId,
    moduleId: props.moduleId,
  });
  const { state, submit, saveStay, flushCurrentAnswer, advanceNext, retry, goBack, canSubmit, isComplete, hasError, isLoading } = flow;
  const slot = state.currentSlot;
  const question = slot?.question as AdaptiveQuestion | undefined;
  const navigability = slot?.navigability;
  const priorAnswer = slot?.priorAnswer;

  // When the parent owns the terminal handling, hand off as soon as
  // this module reports complete. The parent typically responds by
  // remounting with the next module's id (via a key change), so this
  // component never shows its own "module complete" stub.
  const { onModuleComplete } = props;
  useEffect(() => {
    if (isComplete && onModuleComplete) onModuleComplete();
  }, [isComplete, onModuleComplete]);

  // Report the first sighting of a stand-alone block so the container can
  // title the end-of-section screen precisely (see onStandaloneItemsSeen).
  const { onStandaloneItemsSeen } = props;
  const hasStandaloneItems = navigability?.standaloneItems != null;
  useEffect(() => {
    if (hasStandaloneItems && onStandaloneItemsSeen) onStandaloneItemsSeen();
  }, [hasStandaloneItems, onStandaloneItemsSeen]);

  const [pendingMcChoice, setPendingMcChoice] = useState<number | null>(null);
  // Type 3 (multi-response) / type 10 (hot-spot figure): list of
  // selected choice ids. Same shape, different rendering.
  const [pendingMultiResponse, setPendingMultiResponse] = useState<number[]>([]);
  const [pendingHotText, setPendingHotText] = useState<number[]>([]);
  const [pendingFillIn, setPendingFillIn] = useState<FillInPair[]>([]);
  // Type 9 (inline choice): list of [dropdownIndex, choiceString] pairs.
  const [pendingInlineChoice, setPendingInlineChoice] = useState<InlineChoicePair[]>([]);
  // Type 5 (D&D Buckets): list of [bucketId, itemId] pairs.
  const [pendingBucket, setPendingBucket] = useState<BucketPair[]>([]);
  // Type 6 (D&D Blanks): list of [itemId, blankIndex] pairs.
  const [pendingBlank, setPendingBlank] = useState<BlankPair[]>([]);
  // Type 8 (Table Grid): list of [rowId, columnId] pairs.
  const [pendingTableGrid, setPendingTableGrid] = useState<TableGridPair[]>([]);
  // Types 11/12 (Equation Editor): MathLive LaTeX/numeric string.
  const [pendingEquation, setPendingEquation] = useState<string>('');
  // Armed when the student clicks Next on the last slot of a passage:
  // the answer is persisted WITHOUT advancing (saveStay) and the
  // end-of-passage interstitial (page 11 of the printed test) is shown.
  // The interstitial's blue forward arrow then calls advanceNext (which
  // serves the next passage and locks this one); back / Review re-enter
  // the passage. Declared up here with the other hooks — declaring it
  // after the early returns would trip the "rendered fewer hooks"
  // runtime error on state transitions.
  const [interstitialArmed, setInterstitialArmed] = useState(false);
  // Reading-comp directions overlay — re-showable, opened from the Review
  // list's leading "ELA Rdg Comp Directions" row (first reading set only). The
  // blue forward / back arrow dismisses it. Transient (not acked).
  const [showReadingDirections, setShowReadingDirections] = useState(false);
  // Slot numbers whose section-directions screen has been dismissed (forward
  // arrow). A Set so back-nav into the slot doesn't re-show the screen, and
  // so it shows once per slot regardless of re-renders. Resets per module
  // (parent remounts on module change via key), matching the timer/interstitial.
  const [directionsAck, setDirectionsAck] = useState<Set<number>>(new Set());
  // Passage ids whose per-set START screen has been dismissed (Start
  // button / forward arrow). A Set so it shows once per passage set;
  // resets per module (the parent remounts AdaptiveModuleInner on module
  // change via key), matching directionsAck.
  const [passageStartAck, setPassageStartAck] = useState<Set<string>>(new Set());
  // First slot number of the unified stand-alone block whose START screen has
  // been dismissed. Keyed by slot number. Resets per module like passageStartAck.
  const [standaloneStartAck, setStandaloneStartAck] = useState<Set<number>>(new Set());
  // EPHEMERAL per-passage-set bookmarks (no DB). Slot numbers bookmarked in the
  // CURRENT passage set; reset whenever the passage changes (moving to another
  // set), matching the official per-set semantics.
  const [bookmarkedSlots, setBookmarkedSlots] = useState<Set<number>>(new Set());
  useEffect(() => {
    setBookmarkedSlots(new Set());
  }, [slot?.passageId]);
  // Per-slot timer. Records the mount time of each NEW slot; on
  // submit, computes elapsed seconds and sends as ``timeSpent``. Without
  // this, every save POSTs ``timeSpent: 0`` and the tutor's review +
  // report screens show 0:00 for every question.
  //
  // Resets only on FORWARD progression (first time a slot is seen).
  // Back navigation revisits an already-seen slot — resetting the timer
  // there would let a revise-and-submit overwrite the slot's original
  // recorded time with only the revision window, losing the genuine
  // first-pass duration. ``slotsTimedRef`` tracks which slot numbers
  // we've already started the timer for so back-nav is a no-op.
  const slotStartRef = useRef<number>(Date.now());
  const slotsTimedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const n = slot?.slotNumber;
    if (n == null) return;
    if (!slotsTimedRef.current.has(n)) {
      slotsTimedRef.current.add(n);
      slotStartRef.current = Date.now();
    }
  }, [slot?.slotNumber]);
  const [toolsSelected, setToolsSelected] = useState<AdaptiveTool>('Pointer');
  // Per-slot cross-out state for the Answer Eliminator. Clears on slot
  // change and is persisted server-side in the save payload's
  // ``crossedOut`` (StudentCrossedOutMultipleChoiceAnswer), matching legacy.
  const [crossedOut, setCrossedOut] = useState<Set<number>>(new Set());
  // Current passage's highlights, lifted out of PassageColumn (its local
  // state) via ``onAnnotationsChange`` so the save payload can persist them
  // server-side (StudentPassageAnnotation), matching legacy. A ref (not
  // state) avoids re-rendering the whole module on every highlight.
  const annotationsRef = useRef<PassageAnnotation[]>([]);
  // Stable so PassageColumn's push-up effect doesn't refire every render.
  const handleAnnotationsChange = useCallback((anns: PassageAnnotation[]) => {
    annotationsRef.current = anns;
  }, []);
  const [dropdownToolsSelected, setDropdownToolsSelected] = useState<string[]>([]);
  // Alert popup shown when the student tries to exceed the max number
  // of HotText / multi-response selections. Auto-hides on next slot.
  const [alertOpen, setAlertOpen] = useState<{ max: number } | null>(null);
  // Holds a minimum 120ms "loader showing" window after the student
  // clicks Next, so the LoadingPopup is always perceptible even when
  // the network round-trip is fast. Mirrors legacy Module/index.tsx:
  // 1237-1241's 200ms loading flash per questionNumber change.
  const [submitLoading, setSubmitLoading] = useState(false);
  const submitLoadingTimeoutRef = useRef<number | null>(null);
  const [definitionPopup, setDefinitionPopup] = useState<{
    text: string;
    definition: string;
    x: number;
    y: number;
  } | null>(null);
  const questionAreaRef = useRef<HTMLDivElement>(null);

  // Reset the per-slot pending answer whenever the slot changes. When
  // the server included a ``priorAnswer`` (back-navigation re-serve),
  // hydrate the pending state from it so the student sees their prior
  // submission and can edit it in place. The hydration is keyed on
  // question type — the shape mirrors what we submit.
  useEffect(() => {
    setPendingMcChoice(null);
    setPendingMultiResponse([]);
    setPendingHotText([]);
    setPendingFillIn([]);
    setPendingInlineChoice([]);
    setPendingBucket([]);
    setPendingBlank([]);
    setPendingTableGrid([]);
    setPendingEquation('');
    setDefinitionPopup(null);
    setCrossedOut(new Set());
    annotationsRef.current = [];
    setAlertOpen(null);

    if (!priorAnswer || !question) return;
    const value = priorAnswer.answer;
    const qt = question.questionType;
    if (qt === 1) {
      setPendingMcChoice(typeof value === 'number' ? value : null);
    } else if (qt === 3 || qt === 10) {
      setPendingMultiResponse(Array.isArray(value) ? (value as number[]) : []);
    } else if (qt === 4) {
      setPendingHotText(Array.isArray(value) ? (value as number[]) : []);
    } else if (qt === 5) {
      setPendingBucket(Array.isArray(value) ? (value as BucketPair[]) : []);
    } else if (qt === 6) {
      setPendingBlank(Array.isArray(value) ? (value as BlankPair[]) : []);
    } else if (qt === 7) {
      setPendingFillIn(Array.isArray(value) ? (value as FillInPair[]) : []);
    } else if (qt === 8) {
      setPendingTableGrid(Array.isArray(value) ? (value as TableGridPair[]) : []);
    } else if (qt === 9) {
      setPendingInlineChoice(Array.isArray(value) ? (value as InlineChoicePair[]) : []);
    } else if (qt === 11 || qt === 12) {
      setPendingEquation(typeof value === 'string' ? value : '');
    }
  }, [slot?.slotNumber, priorAnswer, question]);

  // Clear the submit-loading minimum-display timer on unmount so we
  // don't leak the timer or fire setState after unmount.
  useEffect(() => {
    return () => {
      if (submitLoadingTimeoutRef.current !== null) {
        window.clearTimeout(submitLoadingTimeoutRef.current);
      }
    };
  }, []);

  // handleToolChange mirrors Module/index.tsx:1163 — clicking the same
  // tool a second time deselects it (returns to the Pointer default).
  // Without this, the Notepad popup and Eliminator state would be
  // sticky and require a different button to dismiss.
  const handleToolChange = (tool: AdaptiveTool) => {
    setToolsSelected((prev) => (prev === tool ? 'Pointer' : tool));
  };

  const toggleCrossOut = (choiceId: number) => {
    setCrossedOut((prev) => {
      const next = new Set(prev);
      if (next.has(choiceId)) next.delete(choiceId);
      else next.add(choiceId);
      return next;
    });
    // Selecting cross-out implicitly clears the chosen answer when the
    // student crosses out the option they had selected (mirrors legacy
    // semantics: a crossed-out choice cannot also be the picked answer).
    // Legacy's crossOutChoice filtered the choice out of the answer whether
    // it was a single value or an array, so this must clear BOTH the
    // single-select (MC) and multi-select (MR) pending answers — otherwise a
    // "select two" choice stays selected while struck through.
    if (pendingMcChoice === choiceId) setPendingMcChoice(null);
    setPendingMultiResponse((prev) => prev.filter((x) => x !== choiceId));
  };

  const toggleDropdownTool = (tool: string) => {
    setDropdownToolsSelected((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool],
    );
  };

  // Attach the Line Reader mask's drag + resize handlers. Mirrors the
  // legacy wiring in Module/index.tsx (the four dragMove/popupResize
  // calls). The mask element is always in the DOM (class-toggled, not
  // unmounted), but we re-attach when the dropdown toggles or the slot
  // changes so the handlers survive body re-renders across slots.
  useEffect(() => {
    const dragEl = document.getElementById('lineReaderDrag');
    const parentEl = document.getElementById('digitalSHSATModule__body');
    if (dragEl && parentEl) {
      // body === element (same id) — the whole mask is the drag handle;
      // allowOverflow=true so the mask can extend past the body edges.
      dragMove(dragEl, dragEl, parentEl, undefined, 0, true);
    }

    const controlDragEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
    const controlBarEl = document.getElementById('digitalSHSATModule__lineReaderControlBar');
    const lineReaderParentEl = document.getElementById('digitalSHSATModule__popuplineReader');
    if (controlDragEl && controlBarEl && lineReaderParentEl) {
      dragMove(controlDragEl, controlBarEl, lineReaderParentEl, undefined, 20);
    }

    const resizeEl = document.getElementById('lineReaderDrag');
    const resizeIconEl = document.getElementById('digitalSHSATModule__lineReaderResize');
    const resizeChildEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
    if (resizeEl && resizeIconEl && parentEl && resizeChildEl) {
      popupResize(resizeEl, resizeIconEl, parentEl, resizeChildEl);
    }

    const transparentResizeEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
    const transparentResizeIconEl = document.getElementById('digitalSHSATModule__lineReaderTransparentResize');
    const transparentResizeParentEl = document.getElementById('lineReaderDrag');
    if (transparentResizeEl && transparentResizeIconEl && transparentResizeParentEl) {
      popupResize(transparentResizeEl, transparentResizeIconEl, transparentResizeParentEl);
    }
  }, [dropdownToolsSelected, slot?.slotNumber]);

  // KaTeX-render the question text + MC choice text after each slot.
  // The legacy SHSAT module does the equivalent via a ``renderKatex()``
  // sweep over all ``.katex`` elements (Module/index.tsx:1121-1133).
  // Without this, ``$x^2$`` in question stems shows raw LaTeX.
  useEffect(() => {
    if (!questionAreaRef.current || !question) return;
    // Snapshot the manual `.katex` spans (stem + choices) BEFORE rendering;
    // renderMathInElement injects nested `.katex` we don't want to re-process.
    const katexEls = Array.from(
      questionAreaRef.current.getElementsByClassName('katex'),
    ) as HTMLElement[];
    katexEls.forEach((el) => {
      renderMathInElement(el, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$', right: '$', display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        trust: true,
      });
      // KaTeX emits non-breaking spaces between words; swap them for normal
      // spaces so long text can actually break (matches legacy renderKatex,
      // Module/index.tsx:1183).
      el.innerHTML = el.innerHTML.replaceAll('&nbsp;', ' ');
    });
    // Let ELA KaTeX text wrap so a long bold quoted sentence (`$\textbf{...}$`
    // in "Read this sentence..." stems) and long combine-sentence choices
    // break inside the question box instead of overflowing. The global
    // `.katex-html .wrap` rule applies `display:inline; white-space:normal` —
    // CSS alone can't do this because KaTeX's `.base` is `inline-block` and
    // shrink-wraps. Skip Math so equation layout (inline-block bases) is
    // preserved. Matches legacy renderKatex (Module/index.tsx:1186-1194).
    const isMath = (props.sectionName ?? '').toLowerCase().includes('math');
    applyKatexWrap(questionAreaRef.current, { enabled: !isMath });
    // A wrapped KaTeX underline paints its lines on top of each other;
    // rebuild it as wrappable text (italic/bold kept), as every other player does.
    flattenKatexUnderlines(questionAreaRef.current);
  }, [question?.id]);

  // Timer-expiry plumbing. Declared BEFORE the early returns below — hooks
  // after a conditional return crash with React #310 ("rendered more hooks
  // than during the previous render") on the loading→ready transition. The
  // ref's closure is (re)assigned further down, after ``hasAnswer`` and
  // ``dispatchPendingAnswer`` exist.
  const timeExpiredRef = useRef(false);
  const runTimeExpiryRef = useRef<() => void | Promise<void>>();
  const handleTimeExpired = useCallback(() => {
    void runTimeExpiryRef.current?.();
  }, []);

  if (hasError) {
    return (
      <FullPageMessage>
        <h2>Something went wrong</h2>
        <pre style={{ color: '#b00', whiteSpace: 'pre-wrap' }}>{state.error}</pre>
        <Button type="button" onClick={retry}>Retry</Button>
      </FullPageMessage>
    );
  }

  if (isComplete) {
    // Parent-driven: don't render anything here — the parent's
    // onModuleComplete handler will swap us out. The empty state is
    // momentary because the swap fires synchronously from useEffect.
    // Use the same dimmed LoadingPopup as legacy (not a stark full-page
    // text) so the module-boundary transition looks like the legacy loader.
    if (onModuleComplete) return <LoadingPopup />;
    // Standalone (no parent callback): show the terminal screen.
    return (
      <FullPageMessage>
        <h2>Module complete</h2>
        <p>
          All slots answered. The score report + review screen mount here in
          a follow-up pass — for now this is the smoke endpoint of the flow.
        </p>
      </FullPageMessage>
    );
  }

  if (!question) {
    // Initial load (and any genuine no-question moment): show the legacy
    // dimmed LoadingPopup instead of a blank full-page "Loading…" text.
    return <LoadingPopup />;
  }

  const qType = question.questionType;
  const isMC = qType === 1 && Array.isArray(question.choices);
  // Type 3 (multi-response): radio-style list of selectable text/figure
  // choices, multi-select. Type 10 (hot-spot figure): same answer shape
  // (list of choice ids), but rendered as a grid of figure tiles with a
  // blue outline on selection. Both share ``pendingMultiResponse`` state.
  const isMultiResponse = qType === 3 && Array.isArray(question.choices);
  const isHotSpot = qType === 10 && Array.isArray(question.choices);
  const isHotText = qType === 4;
  const isFillIn = qType === 7;
  const isInlineChoice = qType === 9 && Array.isArray(question.inlineChoices);
  const isBucket = qType === 5
    && Array.isArray(question.buckets)
    && Array.isArray(question.dragAndDropItems);
  const isBlank = qType === 6 && Array.isArray(question.dragAndDropItems);
  const isTableGrid = qType === 8
    && Array.isArray(question.tableGridRows)
    && Array.isArray(question.tableGridColumns);
  const isEquation = qType === 11 || qType === 12;

  // No-skip: submit only enabled when student has provided an answer
  // for the current question type. Parity rule with the LEGACY locked
  // flow (``isLockQuestionAnswered``): a question counts as answered as
  // soon as it has ANY non-empty value — NOT "every blank filled". The
  // legacy fixed-form player is the reference format, so a partially
  // filled multi-blank/inline-choice/bucket lets the student advance,
  // exactly as it does on a legacy locked test. (A malformed question
  // with zero slots unlocks the arrow instead of deadlocking the test.)
  let hasAnswer = false;
  if (isMC) hasAnswer = pendingMcChoice !== null;
  else if (isMultiResponse) hasAnswer = pendingMultiResponse.length > 0;
  else if (isHotSpot) hasAnswer = pendingMultiResponse.length > 0;
  else if (isHotText) hasAnswer = pendingHotText.length > 0;
  else if (isFillIn) {
    const slots = question.inputSlots ?? 0;
    hasAnswer = slots === 0 || pendingFillIn.some(([, v]) => (v ?? '').trim().length > 0);
  } else if (isInlineChoice) {
    const total = question.inlineChoices?.length ?? 0;
    hasAnswer = total === 0 || pendingInlineChoice.some(([, v]) => (v ?? '').trim().length > 0);
  } else if (isBucket) {
    hasAnswer = pendingBucket.length > 0;
  } else if (isBlank) {
    hasAnswer = pendingBlank.length > 0;
  } else if (isTableGrid) {
    hasAnswer = pendingTableGrid.length > 0;
  } else if (isEquation) {
    hasAnswer = pendingEquation.trim().length > 0;
  }

  const submitEnabled = canSubmit && hasAnswer;

  // Show loader for at least 120ms after a save click. The network
  // round-trip (save → serve) typically takes 150-500ms; the minimum
  // window guarantees the popup is always visible so the click feels
  // responsive even when the round-trip is faster. Combined with
  // ``isLoading`` from the hook (true while save/serve is in flight),
  // this gives the same loading flash as legacy.
  const runSubmitLoaderWindow = () => {
    setSubmitLoading(true);
    if (submitLoadingTimeoutRef.current !== null) {
      window.clearTimeout(submitLoadingTimeoutRef.current);
    }
    submitLoadingTimeoutRef.current = window.setTimeout(() => {
      setSubmitLoading(false);
      submitLoadingTimeoutRef.current = null;
    }, 120);
  };

  // Send the current slot's pending answer (shape varies by question
  // type) through ``action`` — either ``submit`` (save + advance) or
  // ``saveStay`` (persist only, for the interstitial). ``timeSpent`` is
  // the elapsed seconds since this slot mounted, so review/report
  // screens show real per-question times.
  const dispatchPendingAnswer = (
    action: (answer: unknown, options?: SubmitOptions) => void | Promise<void>,
  ): void | Promise<void> => {
    const elapsed = Math.max(0, Math.round((Date.now() - slotStartRef.current) / 1000));
    // Persist highlights + answer-eliminator cross-outs alongside the answer
    // (legacy parity). ``annotations`` holds the current passage's highlights;
    // ``crossedOut`` holds this question's eliminated choices.
    const opts: SubmitOptions = {
      timeSpent: elapsed,
      annotations: annotationsRef.current.map((a) => ({
        passageId: a.passageId,
        startIndex: a.startIndex,
        endIndex: a.endIndex,
        color: a.color,
      })),
      crossedOut: Array.from(crossedOut),
    };
    // Return the action's result so callers can await it (used by the
    // timer-expiry flush). Answer-shape flags are mutually exclusive.
    if (isMC) return action(pendingMcChoice, opts);
    if (isMultiResponse) return action(pendingMultiResponse, opts);
    if (isHotSpot) return action(pendingMultiResponse, opts);
    if (isHotText) return action(pendingHotText, opts);
    if (isFillIn) return action(pendingFillIn, opts);
    if (isInlineChoice) return action(pendingInlineChoice, opts);
    if (isBucket) return action(pendingBucket, opts);
    if (isBlank) return action(pendingBlank, opts);
    if (isTableGrid) return action(pendingTableGrid, opts);
    if (isEquation) return action(pendingEquation, opts);
    return undefined;
  };

  const doSubmit = () => {
    if (!submitEnabled) return;
    runSubmitLoaderWindow();
    dispatchPendingAnswer(submit);
  };

  // Forward action from a question. On the LAST question of a passage,
  // persist without advancing and arm the end-of-passage interstitial
  // (the passage doesn't lock until the interstitial's blue forward
  // arrow). Standalone (no passage) or non-last slots advance directly.
  const handleSubmit = () => {
    if (!submitEnabled) return;
    if (navigability?.isLastInPassage) {
      setInterstitialArmed(true);
      runSubmitLoaderWindow();
      dispatchPendingAnswer(saveStay);
      return;
    }
    doSubmit();
  };

  // Timer-expiry flush. When the total clock hits 0:00 the parent jumps
  // straight to the submit gate; without this, an answer the student had
  // selected on the current (final Math) slot but not yet advanced past is
  // lost — the per-slot save only fires on advance, and the last slot has
  // no advance. So we persist the pending answer (best-effort, time-bounded
  // so it never blocks the mandatory auto-submit) BEFORE handing control
  // back to the parent's ``onTimeExpired`` (which triggers the submit).
  // The latest closure lives in a ref because the always-mounted top-bar
  // timer captures ``onTimeExpired`` once; a stable wrapper keeps it fresh.
  // (The refs + the ``handleTimeExpired`` wrapper are declared above the
  // early returns — hook-order rule — only the closure is assigned here.)
  runTimeExpiryRef.current = async () => {
    if (timeExpiredRef.current) return;
    timeExpiredRef.current = true;
    if (state.status === 'ready' && hasAnswer) {
      const flushed = dispatchPendingAnswer(flushCurrentAnswer);
      if (flushed && typeof (flushed as Promise<void>).then === 'function') {
        await Promise.race([
          flushed as Promise<void>,
          new Promise<void>((resolve) => window.setTimeout(resolve, 4000)),
        ]).catch(() => undefined);
      }
    }
    props.onTimeExpired?.();
  };

  // Whether the end-of-passage interstitial is on screen. Armed by
  // handleSubmit, shown once the persist-only save settles back to
  // ``ready`` on the same (last-in-passage) slot.
  const showInterstitial =
    interstitialArmed &&
    state.status === 'ready' &&
    !showReadingDirections &&
    !!navigability?.isLastInPassage;

  // Blue forward arrow on the interstitial: leave the passage (serve the
  // next slot, which locks this one).
  const advancePastPassage = () => {
    setInterstitialArmed(false);
    advanceNext();
  };

  // Back / Review from the interstitial re-enter the passage.
  const reenterPassageAt = (slotNumber: number) => {
    setInterstitialArmed(false);
    goBack(slotNumber);
  };

  // ONE unified ELA "Stand alone items" (Revising/Editing Part A + Part B)
  // START, shown BEFORE the block's first slot. ``navigability.standaloneItems``
  // (server: from the earliest of the Part A marker / Part B marker / no-marker
  // trailing run, through the section end) is set for EVERY block slot —
  // including Part A's shared-passage slots. ``position === 1`` marks the
  // block's first slot; it's driven purely by ``standaloneItems`` (Part A/B
  // items may carry a passage). Never set on Math. This START appears BEFORE
  // the Part A / Part B directions (the directions are sub-steps that follow).
  const standaloneItems = navigability?.standaloneItems ?? null;
  const standaloneStartPending =
    state.status === 'ready' &&
    slot?.slotNumber != null &&
    standaloneItems != null &&
    standaloneItems.position === 1 &&
    !standaloneStartAck.has(slot.slotNumber) &&
    !showReadingDirections &&
    !showInterstitial;

  // Section-directions interstitial (Revising/Editing Part A / Part B). Shown
  // BEFORE a slot whose workbook marker resolved to a directions prompt, once
  // per slot. Never co-shown with the end-of-passage interstitial, and AFTER
  // the stand-alone START (the START is the section entry; the Part A / Part B
  // directions are sub-steps that follow it).
  const sectionDirectionsPrompt = resolveSectionDirections(slot?.sectionDirections);
  const showSectionDirections =
    !!sectionDirectionsPrompt &&
    state.status === 'ready' &&
    slot?.slotNumber != null &&
    !directionsAck.has(slot.slotNumber) &&
    !standaloneStartPending &&
    !showReadingDirections &&
    !showInterstitial;
  const acknowledgeDirections = () => {
    if (slot?.slotNumber == null) return;
    const n = slot.slotNumber;
    setDirectionsAck((prev) => {
      const next = new Set(prev);
      next.add(n);
      return next;
    });
  };

  // Per-passage-set START screen ("SECTION – Passage Set / Start"), shown
  // BEFORE each READING passage set. Detected from what the serve payload
  // exposes: adaptive serving is sequential, so the current slot is a set's
  // first question exactly when it's the ONLY served slot of its passage group
  // (``navigability.passageSlots``). Suppressed inside the stand-alone block
  // (``standaloneItems != null`` — Part A shares a passage but is NOT a reading
  // passage set). Math slots have no passage -> never shown. Section directions
  // take precedence.
  const passageSlotsServed = navigability?.passageSlots ?? [];
  const showPassageSetStart =
    state.status === 'ready' &&
    slot?.passageId != null &&
    standaloneItems == null &&
    passageSlotsServed.length === 1 &&
    passageSlotsServed[0]?.slotNumber === slot.slotNumber &&
    !passageStartAck.has(slot.passageId) &&
    !showSectionDirections &&
    !showReadingDirections &&
    !showInterstitial;
  const acknowledgePassageSetStart = () => {
    if (slot?.passageId == null) return;
    const passageId = slot.passageId;
    setPassageStartAck((prev) => {
      const next = new Set(prev);
      next.add(passageId);
      return next;
    });
  };

  // The single "Stand alone items" START (computed above as
  // standaloneStartPending, so it can gate the directions to appear after it).
  const showStandaloneStart = standaloneStartPending;
  const acknowledgeStandaloneStart = () => {
    if (slot?.slotNumber == null) return;
    const n = slot.slotNumber;
    setStandaloneStartAck((prev) => {
      const next = new Set(prev);
      next.add(n);
      return next;
    });
  };

  return (
    <div className="digitalSHSAT">
      <div className="digitalSHSAT__box">
        <div id="digitalSHSATModule" className="digitalSHSATModule">
          {/* Notepad popup mounts here so its draggable parent boundary
              is the digitalSHSATModule wrapper, matching legacy. */}
          <GlobalNotepad
            isOpen={toolsSelected === 'Global Notepad'}
            studentTestId={props.studentTestId}
            onClose={() => handleToolChange('Global Notepad')}
          />
          <AdaptiveTopBar
            testName={props.testName ?? null}
            sectionName={props.sectionName ?? null}
            durationSeconds={props.totalDurationSeconds ?? props.moduleDurationSeconds ?? null}
            onTimeExpired={handleTimeExpired}
            eliminatorEnabled={isMC || isMultiResponse}
            studentTestId={props.studentTestId}
            moduleId={props.moduleId}
            slotNumber={slot?.slotNumber ?? null}
            moduleFirstSlotNumber={props.moduleFirstSlot ?? null}
            totalSlots={props.totalSlots ?? 0}
            moduleSlotCount={props.moduleSlotCount ?? null}
            // Passage-set framing for ELA slots — "PASSAGE SET X OF N" +
            // per-set "n OF m". Null for Math/standalone (global label).
            passageSet={navigability?.passageSet ?? null}
            // Unified stand-alone block framing — "STAND ALONE ITEMS" +
            // per-block "n OF size". Null for reading / Math.
            standaloneItems={standaloneItems}
            studentName={props.studentName ?? null}
            toolsSelected={toolsSelected}
            onToolChange={handleToolChange}
            dropdownToolsSelected={dropdownToolsSelected}
            onDropdownToolChange={toggleDropdownTool}
            onOpenContrast={props.onOpenContrast}
            onDiscardExit={props.onDiscardExit}
            onAdvance={showReadingDirections ? () => setShowReadingDirections(false) : showStandaloneStart ? acknowledgeStandaloneStart : showSectionDirections ? acknowledgeDirections : showPassageSetStart ? acknowledgePassageSetStart : showInterstitial ? advancePastPassage : handleSubmit}
            canAdvance={(showReadingDirections || showStandaloneStart || showSectionDirections || showPassageSetStart || showInterstitial) ? true : submitEnabled}
            onBack={() => {
              // Reading-comp directions overlay: back dismisses it.
              if (showReadingDirections) {
                setShowReadingDirections(false);
                return;
              }
              if (!slot || !navigability?.canGoBack) return;
              // Use the server-supplied prev slot number rather than
              // arithmetic — keeps the client robust if slot numbering
              // ever becomes non-sequential.
              const prev = navigability.prevSlotNumber;
              if (prev == null) return;
              // Back from the interstitial re-enters the passage.
              setInterstitialArmed(false);
              // Mirror the forward-submit minimum-visible-loader window
              // (see doSubmit) so the popup looks identical on a fast
              // network round-trip whether the student went back or
              // forward.
              runSubmitLoaderWindow();
              goBack(prev);
            }}
            canGoBack={navigability?.canGoBack ?? false}
            // Passage-set Review/Bookmark are available DURING the passage set
            // (not only at the end-of-passage interstitial), scoped to the
            // current set's served slots.
            passageSlots={navigability?.passageSlots ?? []}
            passageGroupSize={navigability?.passageGroupSize ?? null}
            currentSlotNumber={slot?.slotNumber ?? null}
            onReviewJump={(slotNumber) => {
              setShowReadingDirections(false);
              if (slotNumber === slot?.slotNumber) return;
              runSubmitLoaderWindow();
              reenterPassageAt(slotNumber);
            }}
            // Review "Passage End Directions" row → show the end-of-passage
            // interstitial. If already on the passage's last slot, just arm it;
            // otherwise re-serve the last slot and arm — the interstitial shows
            // once that slot loads (armed + ready + isLastInPassage).
            onPassageEndPage={showInterstitial}
            onReviewJumpToPassageEnd={() => {
              setShowReadingDirections(false);
              const served = navigability?.passageSlots ?? [];
              const lastSlot = served.length
                ? Math.max(...served.map((s) => s.slotNumber))
                : slot?.slotNumber ?? null;
              if (lastSlot == null) return;
              if (slot?.slotNumber === lastSlot) {
                setInterstitialArmed(true);
                return;
              }
              runSubmitLoaderWindow();
              setInterstitialArmed(true);
              goBack(lastSlot);
            }}
            // Review "ELA Rdg Comp Directions" row (first reading set only) →
            // show the reading-comp directions overlay.
            onReadingDirectionsPage={showReadingDirections}
            onReviewJumpToReadingDirections={() => setShowReadingDirections(true)}
            bookmarkedSlots={Array.from(bookmarkedSlots)}
            isBookmarked={slot?.slotNumber != null && bookmarkedSlots.has(slot.slotNumber)}
            onToggleBookmark={() => {
              if (slot?.slotNumber == null) return;
              const n = slot.slotNumber;
              setBookmarkedSlots((prev) => {
                const next = new Set(prev);
                if (next.has(n)) next.delete(n);
                else next.add(n);
                return next;
              });
            }}
          />
          <div id="digitalSHSATModule__body" className="digitalSHSATModule__body">
          {/* Line Reader mask — ported from legacy Module/index.tsx so the
              "Show Line Reader Mask" dropdown actually renders the draggable/
              resizable mask object. Drag/resize wiring lives in the effect
              keyed on dropdownToolsSelected (see useEffect below). */}
          <div
            id="lineReaderDrag"
            className={dropdownToolsSelected.includes('Show Line Reader Mask')
              ? 'digitalSHSATModule__popup--lineReader draggable-element'
              : 'digitalSHSATModule__popup--hidden'}
          >
            <div id="digitalSHSATModule__popuplineReader" className="digitalSHSATModule__popuplineReader">
              <div id="digitalSHSATModule__lineReaderResize" className="digitalSHSATModule__lineReaderResize">
                <SiZenn />
              </div>
              <div id="digitalSHSATModule__popuplineReaderContent" className="digitalSHSATModule__popuplineReaderContent">
                <div id="digitalSHSATModule__lineReaderControlBar" className="digitalSHSATModule__lineReaderControlBar draggable-element">
                  <div className="digitalSHSATModule__lineReaderGuideIcon">
                    <IoMdMove />
                  </div>
                </div>
                <div className="digitalSHSATModule__lineReaderIconContainer">
                  <div id="digitalSHSATModule__lineReaderTransparentResize" className="digitalSHSATModule__lineReaderTransparentResize">
                    <SiZenn />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End-of-passage interstitial replaces the passage + question
              while armed; the live top bar (back / Review / blue forward)
              stays above it. The question block is hidden (not unmounted)
              so re-entering the passage keeps its state. */}
          {showInterstitial && <PassageInterstitial />}
          {/* Section directions (Revising/Editing Part A / Part B) shown before
              the slot; the live top bar's blue arrow acknowledges it. */}
          {showSectionDirections && sectionDirectionsPrompt && (
            <SectionDirectionsScreen prompt={sectionDirectionsPrompt} />
          )}
          {/* Reading-comp directions overlay (re-showable via the Review list's
              leading row, first reading set only). Blue arrow dismisses. */}
          {showReadingDirections && <SectionDirectionsScreen prompt={READING_COMP_DIRECTIONS} />}
          {/* Per-passage-set START screen, shown before each ELA passage
              set. Its Start button (or the blue forward arrow) reveals the
              set's first question. Set index/count/size come from the
              server ``navigability.passageSet``, so it renders "Passage Set
              X of N / m Questions" like the legacy player. */}
          {showPassageSetStart && (
            <PassageSetStart
              sectionName={props.sectionName ?? null}
              setIndex={navigability?.passageSet?.index ?? null}
              setCount={navigability?.passageSet?.count ?? null}
              questionCount={navigability?.passageSet?.size ?? null}
              onStart={acknowledgePassageSetStart}
            />
          )}
          {/* ELA "Stand alone items" (Part B) START screen — the counterpart
              of the passage-set START, shown before the stand-alone block.
              Question count from the server ``standaloneItems.size``. */}
          {showStandaloneStart && (
            <PassageSetStart
              sectionName={props.sectionName ?? null}
              label={STANDALONE_ITEMS_LABEL}
              questionCount={standaloneItems?.size ?? null}
              onStart={acknowledgeStandaloneStart}
            />
          )}
          <div
            className="digitalSHSATModule__bodyContainer"
            style={(showInterstitial || showSectionDirections || showPassageSetStart || showStandaloneStart || showReadingDirections) ? { display: 'none' } : undefined}
          >
            {question.passages && (
              <PassageColumn
                // key on passageId so annotations clear when the
                // passage changes, but persist across slots that
                // share a passage.
                key={question.passages.id}
                passage={question.passages}
                onDefinitionShown={setDefinitionPopup}
                onAnnotationsChange={handleAnnotationsChange}
              />
            )}

            {/* ``minWidth: 0`` overrides flex's default ``min-width:
                auto``, which would otherwise let wide content (e.g. a
                type-8 table grid with 5+ long column headers) push this
                column past its 50% share of the body. The inner
                horizontal-scroll wrapper inside TableGridHelper only
                works once this constraint is in place. */}
            <div
              className="digitalSHSATModule__question"
              ref={questionAreaRef}
              style={{ minWidth: 0 }}
            >
              <div className="digitalSHSATModule__questionContent">
                {/* Skip the raw stem when it carries blank markers (``[[ ]]``):
                    for fill-in (7) / inline-choice (9) / blank (6) the helper
                    below re-renders that exact text with inputs, so rendering
                    the stem too produced a duplicate question with raw
                    ``[[78]]`` placeholders. A normal prompt has no ``[[``. */}
                {question.question && !question.question.includes('[[') && (
                  <span className="katex digitalSHSATModule__questionQuestion digitalSHSAT__textWrap">
                    {question.question}
                  </span>
                )}
                {question.figures?.map((figure) => (
                  figure.image && (
                    <img
                      key={figure.id}
                      // Legacy uses ``digitalSHSATModul__figure`` (typo,
                      // missing trailing "e") which has no matching CSS
                      // rule — figures silently render at natural width
                      // and overflow. Using the correct class picks up
                      // the existing ``max-width: 80%`` constraint.
                      className="digitalSHSATModule__figure"
                      src={figure.image}
                      alt={`Figure ${figure.id}`}
                    />
                  )
                ))}
              </div>

              {isMC && question.choices && (
                <McChoices
                  choices={question.choices}
                  selected={pendingMcChoice}
                  onSelect={(id) => {
                    if (toolsSelected === 'Answer Eliminator') {
                      toggleCrossOut(id);
                    } else if (!crossedOut.has(id)) {
                      setPendingMcChoice(id);
                    }
                  }}
                  crossedOut={crossedOut}
                  eliminatorActive={toolsSelected === 'Answer Eliminator'}
                />
              )}

              {isMultiResponse && question.choices && (
                <McChoices
                  choices={question.choices}
                  multi={true}
                  multiSelected={pendingMultiResponse}
                  onMultiToggle={(id) => {
                    if (toolsSelected === 'Answer Eliminator') {
                      toggleCrossOut(id);
                      return;
                    }
                    if (crossedOut.has(id)) return;
                    setPendingMultiResponse((prev) => {
                      if (prev.includes(id)) return prev.filter((x) => x !== id);
                      const max = question.maxSelections;
                      if (max != null && prev.length >= max) {
                        // Legacy parity: maxSelections === 1 silently
                        // replaces (radio-like); >= 2 shows the alert.
                        if (max === 1) return [id];
                        setAlertOpen({ max });
                        return prev;
                      }
                      return [...prev, id];
                    });
                  }}
                  // Disable single-select handlers on multi-response.
                  selected={null}
                  onSelect={() => undefined}
                  crossedOut={crossedOut}
                  eliminatorActive={toolsSelected === 'Answer Eliminator'}
                />
              )}

              {isHotSpot && question.choices && (
                <HotSpotChoices
                  choices={question.choices}
                  selected={pendingMultiResponse}
                  onToggle={(id) => {
                    // Legacy semantics (handleHotSpotFigure at
                    // DigitalSHSAT/index.tsx:413):
                    //   - click same tile -> deselect
                    //   - click new tile, under cap -> add
                    //   - click new tile, at cap, maxSelections === 1
                    //     -> REPLACE silently (radio-like single-select)
                    //   - click new tile, at cap, maxSelections >= 2
                    //     -> alert popup, no state change
                    setPendingMultiResponse((prev) => {
                      if (prev.includes(id)) return prev.filter((x) => x !== id);
                      const max = question.maxSelections;
                      if (max != null && prev.length >= max) {
                        if (max === 1) return [id];
                        setAlertOpen({ max });
                        return prev;
                      }
                      return [...prev, id];
                    });
                  }}
                />
              )}

              {isHotText && (
                <HotTextHelper
                  helper={question.questionHelper}
                  selectedIndices={pendingHotText}
                  onChange={setPendingHotText}
                  maxSelections={question.maxSelections}
                  onMaxExceeded={() => {
                    if (question.maxSelections != null) {
                      setAlertOpen({ max: question.maxSelections });
                    }
                  }}
                />
              )}

              {isInlineChoice && (
                <InlineChoiceHelper
                  helper={question.questionHelper}
                  inlineChoices={question.inlineChoices ?? []}
                  values={pendingInlineChoice}
                  onChange={setPendingInlineChoice}
                />
              )}

              {isFillIn && (
                <FillInTheBlankHelper
                  helper={question.questionHelper}
                  values={pendingFillIn}
                  onChange={setPendingFillIn}
                  // Math fill-ins accept numbers + math symbols only; ELA fill-ins
                  // can be words ("baseload"), so the restriction is math-only.
                  restrictToMath={(props.sectionName ?? '').toLowerCase().includes('math')}
                />
              )}

              {isBucket && (
                <DragAndDropBucketHelper
                  buckets={question.buckets ?? []}
                  items={question.dragAndDropItems ?? []}
                  selected={pendingBucket}
                  onChange={setPendingBucket}
                />
              )}

              {isBlank && (
                <DragAndDropBlankHelper
                  helper={question.questionHelper}
                  items={question.dragAndDropItems ?? []}
                  selected={pendingBlank}
                  onChange={setPendingBlank}
                />
              )}

              {isTableGrid && (
                <TableGridHelper
                  rows={question.tableGridRows ?? []}
                  columns={question.tableGridColumns ?? []}
                  selected={pendingTableGrid}
                  onChange={setPendingTableGrid}
                />
              )}

              {isEquation && (
                <EquationEditorHelper
                  value={pendingEquation}
                  onChange={setPendingEquation}
                  questionType={qType}
                />
              )}

              {!isMC && !isMultiResponse && !isHotSpot && !isHotText
                && !isFillIn && !isInlineChoice
                && !isBucket && !isBlank && !isTableGrid && !isEquation && (
                <p style={{ color: '#999', fontSize: 13, padding: 16 }}>
                  This question type ({question.questionType}) is not yet wired
                  into the adaptive renderer.
                </p>
              )}

            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Magnifier lens — rendered OUTSIDE the body so cloning the body
          content doesn't recurse into the lens. Bare draggable lens; toggled
          off from the tools dropdown ("Disable Magnifier"). */}
      <MagnifierLens
        active={dropdownToolsSelected.includes('Enable Magnifier')}
      />

      {definitionPopup && (
        <DefinitionPopup
          {...definitionPopup}
          onClose={() => setDefinitionPopup(null)}
        />
      )}

      {/* Loader between slot transitions. Visible while either the
          adaptive flow is making network requests (save → serve) or a
          minimum 120ms display window is still active after the click. */}
      {(submitLoading || isLoading) && <LoadingPopup />}

      {/* Max-selection alert (HotText / multi-response). Mirrors legacy
          Module/index.tsx:1755-1764 verbatim. CSS class handles
          show/hide via the appended ``show``/``hide`` modifier. */}
      <div className={`digitalSHSATModule__alertPopup ${alertOpen ? 'show' : 'hide'}`}>
        <div>
          You are permitted a{' '}
          <span className="digitalSHSATModule__bold">
            maximum of {alertOpen?.max ?? 0} choices
          </span>{' '}
          for this question.<br />
          Please unselect one of your choices before making another choice.
        </div>
        <div
          className="digitalSHSATModule__alertPopupCloseIcon"
          onClick={() => setAlertOpen(null)}
        >
          <IoClose />
        </div>
      </div>

      {/* The passage-exit confirmation modal was replaced by the
          end-of-passage interstitial screen (PassageInterstitial, page 11
          of the printed test), rendered in the body above. */}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Passage column — KaTeX render + glossary <mark> DOM walk
// ---------------------------------------------------------------------------


function PassageColumn(props: {
  passage: PassagePayload;
  onDefinitionShown: (popup: {
    text: string;
    definition: string;
    x: number;
    y: number;
  }) => void;
  // Lifts the committed highlights up to the parent so the save payload can
  // persist them server-side (StudentPassageAnnotation), matching legacy.
  onAnnotationsChange?: (annotations: PassageAnnotation[]) => void;
}): JSX.Element {
  const { passage, onDefinitionShown, onAnnotationsChange } = props;
  const rawRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<HTMLParagraphElement>(null);
  const passageContainerRef = useRef<HTMLDivElement>(null);
  const helperRef = useRef<HTMLDivElement>(null);
  const [renderedHtml, setRenderedHtml] = useState<string>('');

  // Annotations are kept in component state, keyed by the in-flight
  // selection's (startIndex, endIndex). The component remounts on
  // passageId change (via key prop at caller), so this state is
  // naturally per-passage. Highlights stack — overlapping selections
  // produce stacked <mark> overlays, matching legacy.
  const [annotations, setAnnotations] = useState<PassageAnnotation[]>([]);
  // Push committed highlights up to the parent so the save payload persists
  // them server-side (StudentPassageAnnotation), matching legacy.
  useEffect(() => {
    onAnnotationsChange?.(annotations);
  }, [annotations, onAnnotationsChange]);
  // In-flight selection (mouseUp on the passage). The AnnotationSHSATHelper
  // popup mounts when this is non-null. Cleared once the student picks
  // a color (save commits) or hits the trash (discard).
  const [currentAnnotation, setCurrentAnnotation] = useState<{
    passageId: string;
    startIndex: number;
    endIndex: number;
    annotation: string;
    html?: string;
    preview?: string;
    color?: string;
  } | null>(null);
  // ``annotation`` mirrors legacy Module/index.tsx:248 — a separate
  // state set by AnnotationSHSATHelper's setColor. Driver state for
  // the save effect below; ``currentAnnotation`` controls the popup's
  // visibility only.
  const [annotation, setAnnotation] = useState<{
    passageId: string;
    startIndex: number;
    endIndex: number;
    annotation: string;
    html?: string;
    preview?: string;
    color?: string;
  } | null>(null);
  const [annotationColor, setAnnotationColor] = useState<string | null>(null);

  // Step 1: KaTeX-render the raw passage in a hidden container.
  // Step 2: strip ``.katex-mathml`` accessibility duplicates that KaTeX
  //         emits alongside its visible HTML. Without this, the
  //         passage's flat textContent contains every glyph TWICE
  //         (visible + mathml), and ``measureSelectionOffsets``'s
  //         ``indexOf(anchorText)`` matches the invisible mathml copy
  //         at the top, producing offsets that don't align with the
  //         visible text — selection appears to silently do nothing.
  //         Worst on poetry passages stored as ``\begin{array}`` (the
  //         whole poem is one KaTeX block, so the mathml dupe is huge).
  //         Legacy strips these in its ``renderKatex()`` sweep at
  //         Module/index.tsx:1132-1135.
  // Step 3: inject glossary <mark> tags via the DOM walker.
  useEffect(() => {
    if (!rawRef.current) return;
    rawRef.current.innerHTML = passage.passage;
    renderMathInElement(rawRef.current, {
      delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '$', right: '$', display: false },
        { left: '\\(', right: '\\)', display: false },
        { left: '\\[', right: '\\]', display: true },
      ],
      trust: true,
    });
    rawRef.current
      .querySelectorAll('.katex-mathml')
      .forEach((el) => el.remove());
    // KaTeX renders every top-level run into a `.base` that is
    // `display:inline-block; white-space:nowrap; width:min-content`, so a
    // passage intro authored as one long `$\textit{...}$` cannot break: it
    // overflows the column and turns the pane into a horizontal scroller with
    // the start of the line clipped. Every other player sweeps this away after
    // rendering -- the legacy SHSAT module, the SHSAT review and TSIA2 all call
    // the shared helper -- but the adaptive player only ever applied it to the
    // question column, never here, so adaptive passages have never wrapped.
    //
    // Must run BEFORE buildPassageWithMarks: that serialises this DOM into the
    // HTML string we actually render, so the `wrap` class has to be on the
    // bases by then.
    //
    // The helper, not a CSS rule: it vetoes `.mtable` bases, whose row offsets
    // are computed at render time. SHSAT verse passages are stored as
    // `$\begin{array}{rl} ... \end{array}$`, and forcing those to reflow lands
    // the lines on top of each other -- see
    // docs/bugfix/20260919-shsat-array-passage-wrap-overlap-plan.md.
    //
    // The `&nbsp;` swap is half the fix, not a tidy-up: KaTeX emits U+00A0 for
    // every text-mode space, so a `wrap`ped base still cannot break at word
    // boundaries and a long intro only splits at a hyphen -- still overflowing.
    // Both the legacy SHSAT module (Module/index.tsx:1247) and TSIA2 do this
    // immediately before their own sweep. U+00A0 and U+0020 are both one
    // character, so the passage's highlight offsets are unaffected.
    rawRef.current.innerHTML = rawRef.current.innerHTML.replaceAll('&nbsp;', ' ');
    applyKatexWrap(rawRef.current, { enabled: true });
    const baseHtml = buildPassageWithMarks(rawRef.current, passage.indices);
    // Passage-level figures are appended to the END of the rendered passage HTML
    // so they flow after the text. They MUST live inside the passage <p> (which is
    // position:absolute as the top layer of the highlight-overlay stack) — the
    // legacy module injects them the same way. Rendering them as separate in-flow
    // siblings (the previous approach) made them collapse to the top of the
    // container and overlap the absolutely-positioned text. Order by source index.
    const figuresHtml = (passage.passageFigures ?? [])
      .filter((pf) => pf.figure?.image)
      .slice()
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map(
        (pf) =>
          '<div class="digitalSHSATModule__passageFigure" style="width:100%;overflow:hidden;margin:10px 0;">'
          + `<img src="${pf.figure.image}" alt="figure" style="width:100%;height:100%;object-fit:contain;" /></div>`,
      )
      .join('');
    setRenderedHtml(baseHtml + figuresHtml);
  }, [passage.id, passage.passage, passage.indices, passage.passageFigures]);

  // Glossary click → definition popup.
  useEffect(() => {
    const el = renderedRef.current;
    if (!el) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName !== 'MARK') return;
      const text = target.textContent ?? '';
      const definition = target.getAttribute('data-definition') ?? '';
      const rect = target.getBoundingClientRect();
      onDefinitionShown({
        text,
        definition,
        x: rect.left,
        y: rect.bottom + window.scrollY,
      });
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [renderedHtml, onDefinitionShown]);

  // Selection → currentAnnotation. Mirrors Module/index.tsx:600.
  const handlePassageMouseUp = () => {
    const offsets = measureSelectionOffsets();
    if (!offsets) {
      setCurrentAnnotation(null);
      return;
    }
    setCurrentAnnotation({
      passageId: offsets.passageId,
      startIndex: offsets.startIndex,
      endIndex: offsets.endIndex,
      annotation: '',
      html: '',
      preview: '',
      color: undefined,
    });
  };

  // When the student picks a color, build the marked-up HTML overlay
  // from the rendered passage DOM and save the annotation. Mirrors
  // Module/index.tsx:873-895 exactly — single ``annotationColor`` dep,
  // reads ``currentAnnotation`` from closure. With both states in the
  // dep array, the initial mouseUp (setting currentAnnotation but no
  // color yet) would also trigger the effect; legacy avoids that by
  // depending on ``annotationColor`` only.
  useEffect(() => {
    if (!currentAnnotation || !annotationColor) return;
    const passageEl = renderedRef.current;
    if (!passageEl) return;
    const { passageHTML, passagePreview } = generatePassageHTML(
      passageEl,
      currentAnnotation.startIndex,
      currentAnnotation.endIndex,
    );
    const updated: PassageAnnotation = {
      passageId: currentAnnotation.passageId,
      startIndex: currentAnnotation.startIndex,
      endIndex: currentAnnotation.endIndex,
      html: passageHTML,
      preview: passagePreview,
      color: annotationColor as PassageAnnotation['color'],
    };
    setAnnotations((prev) => [...prev, updated]);
    setCurrentAnnotation(null);
    setAnnotation(null);
    setAnnotationColor(null);
    window.getSelection()?.removeAllRanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy parity
  }, [annotationColor]);

  // Position the popup centered above the selection. Mirrors
  // Module/index.tsx:897-922.
  useEffect(() => {
    if (!currentAnnotation) return;
    const helper = helperRef.current;
    const container = passageContainerRef.current;
    if (!helper || !container) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    let left = (rect.left - containerRect.left) + (rect.width / 2) - (helper.offsetWidth / 2);
    let top = (rect.top - containerRect.top) - helper.offsetHeight - 15;
    left = Math.max(Math.min(left, containerRect.width - helper.offsetWidth), 0);
    top = Math.max(top, 0);
    helper.style.left = `${left}px`;
    helper.style.top = `${top}px`;
  }, [currentAnnotation]);

  // Delete / un-highlight. Mirrors legacy DigitalSHSAT/index.tsx:659.
  //
  // For each saved annotation that overlaps the current selection range,
  // trim or split it:
  //   - deletion covers the tail of an annotation -> trim its end
  //     down to the deletion's startIndex.
  //   - deletion covers the head of an annotation -> trim its start
  //     up to the deletion's endIndex.
  //   - deletion is strictly inside an annotation -> split into two:
  //     [start..delStart] and [delEnd..end].
  //   - deletion fully covers an annotation -> matches none of the
  //     conditions; legacy leaves the annotation in place (a known
  //     quirk). We mirror that behaviour exactly.
  //
  // After updating indices, each surviving annotation's overlay HTML is
  // regenerated so the rendered marks line up with the new ranges.
  const handleDeleteCurrentAnnotation = () => {
    if (!currentAnnotation) return;
    const passageEl = renderedRef.current;
    if (!passageEl) {
      setCurrentAnnotation(null);
      window.getSelection()?.removeAllRanges();
      return;
    }

    const delStart = currentAnnotation.startIndex;
    const delEnd = currentAnnotation.endIndex;

    const next: PassageAnnotation[] = [];
    annotations.forEach((ann) => {
      let nextStart = ann.startIndex;
      let nextEnd = ann.endIndex;
      const extras: Array<{ start: number; end: number }> = [];

      if (ann.endIndex > delStart && delEnd >= ann.endIndex) {
        nextEnd = delStart;
      } else if (ann.startIndex < delEnd && delStart <= ann.startIndex) {
        nextStart = delEnd;
      } else if (delStart > ann.startIndex && delEnd < ann.endIndex) {
        // Strict split — keep the head [ann.start..delStart] and emit
        // a new annotation for the tail [delEnd..ann.end].
        extras.push({ start: delEnd, end: ann.endIndex });
        nextEnd = delStart;
      }

      if (nextStart < nextEnd) {
        const { passageHTML, passagePreview } = generatePassageHTML(
          passageEl,
          nextStart,
          nextEnd,
        );
        next.push({
          ...ann,
          startIndex: nextStart,
          endIndex: nextEnd,
          html: passageHTML,
          preview: passagePreview,
        });
      }
      extras.forEach(({ start, end }) => {
        const { passageHTML, passagePreview } = generatePassageHTML(
          passageEl,
          start,
          end,
        );
        next.push({
          ...ann,
          startIndex: start,
          endIndex: end,
          html: passageHTML,
          preview: passagePreview,
        });
      });
    });

    setAnnotations(next);
    setCurrentAnnotation(null);
    window.getSelection()?.removeAllRanges();
  };

  return (
    <div className="digitalSHSATModule__material">
      <div className="digitalSHSATModule__materialContent">
        <div ref={passageContainerRef} className="digitalSHSATModule__passage">
          {/* Hidden container used solely to host the KaTeX-rendered
              DOM that the glossary walker reads from. */}
          <div
            ref={rawRef}
            id={passage.id}
            style={{ display: 'none' }}
            dangerouslySetInnerHTML={{ __html: passage.passage }}
          />
          {/* Visible container with glossary marks. Also the
              ``onMouseUp`` target for the highlighter. ID matches the
              prefix the selection walker scrubs (``passage <id>``). */}
          <p
            ref={renderedRef}
            id={`passage ${passage.id}`}
            className="katex digitalSHSATModule__passageTextPassage"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
            onMouseUp={handlePassageMouseUp}
            onTouchEnd={handlePassageMouseUp}
          />
          {/* Passage-level figures are appended into ``renderedHtml`` above (inside
              the passage <p>), not rendered here as separate siblings — see the
              renderedHtml effect. Rendering them as in-flow siblings after the
              absolutely-positioned text made them overlap the text. */}
          {/* Highlight overlays. Each annotation re-renders the entire
              passage HTML with a ``<mark>`` wrapping the selected
              substring; the overlay is stacked over the passage via
              CSS (``digitalSHSATModule__passageAnnotation--<color>``)
              so the underlying text stays selectable. */}
          {annotations.map((a, i) => {
            const inlineBg = a.color === 'Pink'
              ? 'rgba(255,162,162,0.64)'
              : 'rgba(71,169,235,0.62)';
            // Mark inline style: just background + transparent text.
            // Layering mirrors legacy — overlay sits BEHIND the visible
            // <p> (CSS z-index 1 vs 5) so the colored bg renders behind
            // the dark text glyphs (text reads on color, color doesn't
            // cover text). The ``.passageAnnotation`` class has
            // ``color: transparent`` so the overlay's own text doesn't
            // double-render with the visible <p>.
            const html = a.html.replace(
              /<mark>/g,
              `<mark style="background-color:${inlineBg};color:transparent;">`,
            );
            return (
              <div
                key={i}
                className={
                  'katex digitalSHSATModule__passageAnnotation '
                  + (a.color
                    ? `digitalSHSATModule__passageAnnotation--${ANNOTATION_COLOR_CLASS[a.color]}`
                    : '')
                }
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          })}
          {currentAnnotation && (
            <div ref={helperRef} style={{ position: 'absolute' }}>
              <AnnotationSHSATHelper
                id="digitalSHSATModule__passageAnnotationHelper"
                annotation={currentAnnotation}
                annotationColor={annotationColor as string}
                setAnnotationColor={setAnnotationColor}
                setCurrentAnnotation={() => setCurrentAnnotation(null)}
                setAnnotation={setAnnotation as any}
                deleteAnnotation={handleDeleteCurrentAnnotation as any}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// MC choices — visual mirror of legacy
// ---------------------------------------------------------------------------


function McChoices(props: {
  choices: Choice[];
  selected: number | null;
  onSelect: (id: number) => void;
  /** When true, render with multi-select semantics (checkboxes via
   * radio classes) — used for types 3 (multi-response) and 10 (hot
   * spot). The parent owns the toggle state. */
  multi?: boolean;
  multiSelected?: number[];
  onMultiToggle?: (id: number) => void;
  crossedOut?: Set<number>;
  eliminatorActive?: boolean;
}): JSX.Element {
  const {
    choices, selected, onSelect,
    multi, multiSelected, onMultiToggle,
    crossedOut, eliminatorActive,
  } = props;
  const handleClick = (id: number) => {
    if (multi) onMultiToggle?.(id);
    else onSelect(id);
  };
  return (
    <div className="digitalSHSATModule__questionContainer">
      {choices.map((choice, i) => {
        const isSelected = multi
          ? (multiSelected?.includes(choice.id) ?? false)
          : selected === choice.id;
        const isCrossedOut = crossedOut?.has(choice.id) ?? false;

        // Class mirroring Module/index.tsx:2188. When the eliminator is
        // OFF and the choice is crossed-out, the container gets the
        // ``--disabled`` modifier (grays + locks the choice). When the
        // eliminator is ON, the container stays the normal class so the
        // student can re-click to un-cross.
        const containerClass = !eliminatorActive && isCrossedOut
          ? 'digitalSHSATModule__choiceContainer--disabled'
          : 'digitalSHSATModule__choiceContainer';

        // Radio class — also picks up ``--disabled`` when crossed out
        // (mirrors line 2203).
        const radioClass = isCrossedOut
          ? 'digitalSHSATModule__radioButton--disabled'
          : isSelected
            ? 'digitalSHSATModule__radioButton--selected'
            : 'digitalSHSATModule__radioButton';

        return (
          <div key={choice.id} className="digitalSHSATModule__questionChoiceWrapper">
            <div
              className={containerClass}
              onClick={() => handleClick(choice.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClick(choice.id);
              }}
            >
              {/* Eliminator-on: hover line preview (matches legacy 2197). */}
              {eliminatorActive && (
                <div className="digitalSHSATModule__crossOutLine--hover" />
              )}
              {/* Static cross line drawn when the choice is crossed-out
                  (matches legacy 2199-2201). */}
              {isCrossedOut && <div className="digitalSHSATModule__crossOutLine" />}
              <div className={radioClass}>
                {isSelected ? (
                  <IoMdRadioButtonOn className="digitalSHSATModule__radioButtonIcon" />
                ) : (
                  <IoMdRadioButtonOff className="digitalSHSATModule__radioButtonIcon" />
                )}
              </div>
              <div className="digitalSHSATModule__questionChoiceContent">
                <p className="digitalSHSATModule__choiceText">{MULTIPLE_CHOICES[i]}.</p>
                {choice.figure ? (
                  <img
                    className="digitalSHSATModule__questionChoiceFigure"
                    src={choice.figure}
                    alt={`Choice ${MULTIPLE_CHOICES[i]}`}
                  />
                ) : (
                  <p className="digitalSHSATModule__questionChoiceText">
                    <span className="katex digitalSHSAT__textWrap">{choice.choice}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function HotSpotChoices(props: {
  choices: Choice[];
  selected: number[];
  onToggle: (id: number) => void;
}): JSX.Element {
  const { choices, selected, onToggle } = props;
  return (
    <div className="digitalSHSATModule__questionContainerHotSpot">
      {choices.map((choice) => {
        const isSelected = selected.includes(choice.id);
        const cls = isSelected
          ? 'digitalSHSATModule__choiceContainerHotSpot--selected'
          : 'digitalSHSATModule__choiceContainerHotSpot';
        return (
          <div
            key={choice.id}
            className={cls}
            // ``user-select: none`` — without this, even a slight
            // mouse-drag during the click starts an image/text
            // selection, and the click event doesn't fire on the
            // figure until the user releases over a different element.
            //
            // ``z-index: 1`` lifts the SELECTED tile above its
            // unselected siblings so the 5px outline (which paints
            // OUTSIDE the border box and extends into adjacent tiles'
            // space) doesn't get visually covered by the next-clicked
            // tile's body. Without this, on multi-pick questions you
            // see the first selection's outline get clipped where it
            // overlaps with the second clicked tile, which reads as
            // "the highlight only finishes appearing once you click
            // somewhere else."
            // Why inline outline for the selected state:
            // ``global/styles.css:13`` ships ``*:focus { outline: none }``
            // which strips the legacy ``.--selected { outline: 5px solid
            // dodgerBlue }`` rule while the tile is focused (tabIndex=0
            // means clicking it gives it focus). Result: the selection
            // outline only became visible AFTER the student clicked off
            // the tile (focus left). Inline ``style.outline`` wins over
            // the global focus reset regardless of specificity.
            style={{
              userSelect: 'none',
              position: 'relative',
              zIndex: isSelected ? 1 : 0,
              outline: isSelected ? '5px solid var(--dodgerBlue)' : undefined,
            }}
            // ``onPointerDown`` instead of ``onClick`` — clicks via
            // ``onClick`` wait for mouseup on the same element, which
            // in combination with focus shifting from a previous tile
            // can introduce a perceived one-click delay. Pointer-down
            // fires on press, the state updates immediately, and the
            // re-render lands before the student lifts the mouse.
            onPointerDown={() => onToggle(choice.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(choice.id);
              }
            }}
          >
            {choice.figure ? (
              <img
                className="digitalSHSATModule__questionChoiceFigure"
                // ``pointer-events: none`` — clicks pass through the
                // img to the parent div so the whole tile is one big
                // click target. Without this, the img absorbs the
                // pointerdown and the browser may treat the eventual
                // mouseup as the end of a drag, not a click.
                style={{ pointerEvents: 'none' }}
                src={choice.figure}
                alt={`Choice ${choice.id}`}
                draggable={false}
              />
            ) : (
              <p className="digitalSHSATModule__questionChoiceText">
                <span className="katex">{choice.choice}</span>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Glossary popup
// ---------------------------------------------------------------------------


function DefinitionPopup(props: {
  text: string;
  definition: string;
  x: number;
  y: number;
  onClose: () => void;
}): JSX.Element {
  const { text, definition, x, y, onClose } = props;
  return (
    <div
      style={{
        position: 'absolute',
        top: y + 8,
        left: x,
        maxWidth: 320,
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: 12,
        zIndex: 1000,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Glossary Definition</strong>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
        >
          ×
        </button>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.4 }}>{definition || `(no definition for "${text}")`}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Shared shell
// ---------------------------------------------------------------------------


function FullPageMessage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: 20, fontFamily: 'sans-serif' }}>
      {children}
    </div>
  );
}
